import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {minmax} from 'utils/minmax';
import DefaultButton from './DefaultButton';
import DefalutSelectOption from './DefalutSelectOption';
import DefaultText from './DefaultText';
import {getDevice} from 'utils/getDevice';
import moment from 'moment-timezone';
import {ILocale} from 'data/country';
import {useRecoilValue} from 'recoil';
import {localeState} from 'utils/Atoms';
import {useFormatMsg} from 'utils/useFormatMsg';

/**
 * 연도 목록 생성 함수
 * 현재 연도부터 1980년까지의 연도 목록을 생성합니다.
 * @param {ILocale} locale - 로케일
 * @returns {Array<{text: string; value: number}>} 연도 목록
 */
export const getYearList = (locale: ILocale) => {
  moment.locale(locale);

  const currentYear = moment().year();
  const yearList = [];

  for (let year = currentYear; year >= 1980; year--) {
    yearList.push({
      text:
        locale === 'ko-KR'
          ? moment(year.toString()).format('YYYY년')
          : year.toString(),
      value: year,
    });
  }

  return yearList;
};

/**
 * 라벨 연도/월 선택 컴포넌트의 Props 타입
 * @property {{year: number; month: number}} date - 날짜
 * @property {any} setDate - 날짜 설정 함수
 * @property {string} [label] - 라벨 텍스트
 * @property {string} [buttonText] - 버튼 텍스트
 * @property {React.CSSProperties} [labelInputContainerStyle] - 라벨 입력 컨테이너 스타일
 * @property {React.CSSProperties} [labelStyle] - 라벨 스타일
 * @property {React.CSSProperties} [yearContainerStyle] - 연도 컨테이너 스타일
 * @property {React.CSSProperties} [monthContainerStyle] - 월 컨테이너 스타일
 * @property {React.CSSProperties} [buttonStyle] - 버튼 스타일
 * @property {() => void} [onClickButton] - 버튼 클릭 이벤트 핸들러
 * @property {'year' | 'month' | 'both'} [type] - 선택 타입
 */
type Props = {
  date: {
    year: number;
    month: number;
  };
  setDate: any;
  label?: string;
  buttonText?: string;
  labelInputContainerStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  yearContainerStyle?: React.CSSProperties;
  monthContainerStyle?: React.CSSProperties;
  buttonStyle?: React.CSSProperties;
  onClickButton?: () => void;
  type?: 'year' | 'month' | 'both';
};

/**
 * 라벨 연도/월 선택 컴포넌트
 * 라벨과 함께 연도와 월을 선택할 수 있는 컴포넌트입니다.
 * @param {Props} props - 라벨 연도/월 선택 컴포넌트의 props
 * @returns {JSX.Element} 라벨 연도/월 선택 컴포넌트
 */
const LabelSelectYearMonth = ({
  label,
  date,
  setDate,
  buttonText,
  labelInputContainerStyle,
  labelStyle,
  yearContainerStyle,
  monthContainerStyle,
  buttonStyle,
  onClickButton,
  type = 'both',
}: Props) => {
  const device = getDevice();
  const locale = useRecoilValue(localeState);

  const dateList = {
    year: getYearList(locale),
    month: [
      {text: 'jan', value: 1},
      {text: 'feb', value: 2},
      {text: 'mar', value: 3},
      {text: 'apr', value: 4},
      {text: 'may', value: 5},
      {text: 'jun', value: 6},
      {text: 'jul', value: 7},
      {text: 'aug', value: 8},
      {text: 'sep', value: 9},
      {text: 'oct', value: 10},
      {text: 'nov', value: 11},
      {text: 'dec', value: 12},
    ],
  };

  return (
    <LabelInputContainer style={labelInputContainerStyle}>
      {label && (
        <DefaultText
          text={label}
          textStyle={{
            flexShrink: 0,
            ...labelStyle,
          }}
        />
      )}
      {(type === 'both' || type === 'year') && (
        <DefalutSelectOption
          value={date.year}
          setValue={value => setDate({...date, year: value})}
          options={dateList.year}
          containerStyle={yearContainerStyle}
        />
      )}
      {(type === 'both' || type === 'month') && (
        <DefalutSelectOption
          value={date.month}
          setValue={value => setDate({...date, month: value})}
          options={dateList.month}
          containerStyle={monthContainerStyle}
        />
      )}

      {buttonText && (
        <DefaultButton
          text={buttonText}
          actived={1}
          buttonStyle={buttonStyle}
          onClick={onClickButton}
        />
      )}
    </LabelInputContainer>
  );
};

export default LabelSelectYearMonth;

const LabelInputContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: ${minmax(16)};
`;
